import { useEffect, useState } from "react";
import type { UseFormReturn } from "react-hook-form";

import type { AvFormValues } from "../../interfaces/AvFormValues";
import { AvMethodsComponents, getSyncAvMethodVerificationId } from "../../SyncAvWidgets/SyncAvWidgets";

interface UseSyncWidgetButtonStateProps {
  selectedMethod: string;
  isOptional?: boolean;
  form: UseFormReturn<AvFormValues>;
}

/**
 * Controls whether the submit button is disabled while waiting for a sync AV
 * widget to produce a verification id, and exposes the handler that widgets
 * call once they finish.
 */
export function useSyncWidgetButtonState({ selectedMethod, isOptional, form }: UseSyncWidgetButtonStateProps) {
  const [isButtonDisabledForWidgetVerification, setIsButtonDisabledForWidgetVerification] = useState(true);

  useEffect(() => {
    if (isOptional) {
      setIsButtonDisabledForWidgetVerification(false);
      return;
    }

    const isSelectedMethodWidget = (method: string) => Object.hasOwn(AvMethodsComponents, method);

    if (!selectedMethod) {
      return;
    }

    const isSyncMethod = isSelectedMethodWidget(selectedMethod);

    if (!isSyncMethod) {
      setIsButtonDisabledForWidgetVerification(false);
      return;
    }

    const syncMethodVerificationId = getSyncAvMethodVerificationId(selectedMethod);

    setIsButtonDisabledForWidgetVerification(!syncMethodVerificationId);
    form.setValue("avpValues.verification_id", syncMethodVerificationId ?? "");
  }, [selectedMethod, form, isOptional]);

  const handleSyncAvWidgetVerification = (verificationId: string) => {
    form.setValue("avpValues.verification_id", verificationId);
    setIsButtonDisabledForWidgetVerification(false);
  };

  return { isButtonDisabledForWidgetVerification, handleSyncAvWidgetVerification };
}
